import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import {
  Fragment,
  FunctionComponent,
  useCallback,
  useRef,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { cn } from '../../utilities/cn';

interface GenericModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title?: string | JSX.Element;
  description?: string;
  children: React.ReactNode;
  className?: string;
  staticSize?: boolean;
  preventClose?: boolean;
  onClose?: (event: any, reason: string) => void;
  titleClassName?: string;
  descriptionClassName?: string;
}

const GenericModal: FunctionComponent<GenericModalProps> = ({
  isOpen,
  setIsOpen,
  title,
  description,
  children,
  className,
  staticSize = false,
  preventClose = false,
  onClose,
  titleClassName,
  descriptionClassName,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [initialSize, setInitialSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // We are doing this to register after the callback to get the size of the panel
  const handleAfterEnter = useCallback(() => {
    if (!staticSize || initialSize) return;

    const panel = panelRef.current;
    if (panel) {
      const { offsetWidth, offsetHeight } = panel;
      setInitialSize({ width: offsetWidth, height: offsetHeight });
    }
  }, [staticSize, initialSize]);

  const handleClose = (value: boolean) => {
    if (preventClose) {
      return;
    }
    if (onClose) {
      onClose(null, 'close');
    } else {
      setIsOpen(false);
    }
  };

  const handleCloseButton = () => {
    if (preventClose) {
      return;
    }
    if (onClose) {
      onClose(null, 'button');
    } else {
      setIsOpen(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        open={isOpen}
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={handleClose}
      >
        <Overlay />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 pt-16">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
              afterEnter={handleAfterEnter}
            >
              <DialogPanel
                transition
                className={cn(
                  'flex w-full max-w-md transform flex-col rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all',
                  className,
                )}
                style={
                  staticSize && initialSize
                    ? {
                        minWidth: initialSize.width,
                        minHeight: initialSize.height,
                      }
                    : undefined
                }
                ref={panelRef}
              >
                {!preventClose && (
                  <button
                    onClick={handleCloseButton}
                    className="absolute right-4 top-4 flex items-center justify-center rounded-full p-2 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    aria-label="Close"
                  >
                    <CloseIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                  </button>
                )}
                {title && (
                  <DialogTitle
                    as="h3"
                    className={cn(
                      'text-xl font-black text-mainblue',
                      titleClassName,
                    )}
                  >
                    {typeof title === 'string' ? title : <>{title}</>}
                  </DialogTitle>
                )}
                {description && (
                  <p className={cn('mt-2', descriptionClassName)}>
                    {description}
                  </p>
                )}

                <div className="mt-4 min-h-0 flex-1">{children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GenericModal;

export const Overlay: FunctionComponent = () => {
  return (
    <TransitionChild
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-black/25" />
    </TransitionChild>
  );
};
